import { useId, useRef, useState } from 'react';
import { uploadClinicLogo } from '@api/client';
import { useClinicsList } from '@api/queries';
import styles from './FormField.module.css';

/**
 * FormField — renders a single field (text/email/tel/date/number/textarea/
 * select/radio-pills/pills/checkbox) based on its schema definition.
 *
 * Props:
 *   field        — schema field definition { key, label, type, required, options }
 *   value        — current value from form state
 *   onChange     — (newValue) => void
 *   error        — optional string to display
 */
export default function FormField({ field, value, onChange, error, uploadCtx }) {
  const id = useId();
  const labelCls = `${styles.fl} ${field.required ? styles.flReq : ''}`;
  /* Field flag markers (registration): yellow important · green track · red careful */
  const flagDot = field.flag ? <span className={`${styles.flag} ${styles['flag_' + field.flag]}`} title={
    field.flag === 'important' ? 'Important point for doctor attention'
    : field.flag === 'track' ? 'Track in follow-ups'
    : 'Doctor must be careful'
  } /> : null;

  switch (field.type) {
    case 'text':
    case 'email':
    case 'tel':
    case 'date':
    case 'number':
    case 'password': {
      const telMax = field.maxLength ?? 10;
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      // Keep phone numeric and bounded; let email/number surface a hint.
      const handleChange = (e) => {
        let v = e.target.value;
        if (field.type === 'tel') v = v.replace(/\D/g, '').slice(0, telMax);
        onChange(v);
      };
      let localErr = '';
      if (field.type === 'tel' && value && String(value).length !== telMax) {
        localErr = `Enter a ${telMax}-digit number`;
      } else if (field.type === 'email' && value && !emailRe.test(String(value))) {
        localErr = 'Enter a valid email (name@example.com)';
      } else if (field.type === 'number' && value !== '' && value != null
                 && field.min != null && Number(value) < Number(field.min)) {
        localErr = `Must be ${field.min} or more`;
      }
      const showErr = error || localErr;
      return (
        <div className={styles.fg}>
          <label className={labelCls} htmlFor={id}>{field.label}{flagDot}</label>
          {field.hint && <div className={styles.fieldHint}>{field.hint}</div>}
          <input
            id={id}
            className={`${styles.fi} ${showErr ? styles.fiError : ''}`}
            type={field.type}
            value={value ?? ''}
            onChange={handleChange}
            placeholder={field.placeholder || ''}
            readOnly={!!field.readonly}
            inputMode={field.type === 'tel' ? 'numeric' : (field.type === 'number' ? 'decimal' : undefined)}
            maxLength={field.type === 'tel' ? telMax : undefined}
            min={field.type === 'number' ? field.min : undefined}
            step={field.type === 'number' ? (field.step ?? undefined) : undefined}
            aria-invalid={showErr ? 'true' : undefined}
            style={field.readonly ? { background: 'var(--card)', color: 'var(--mu)' } : undefined}
          />
          {showErr && <div className={styles.errText}>{showErr}</div>}
        </div>
      );
    }

    case 'textarea':
      return (
        <div className={styles.fg}>
          <label className={labelCls} htmlFor={id}>{field.label}{flagDot}</label>
          {field.hint && <div className={styles.fieldHint}>{field.hint}</div>}
          <textarea
  id={id}
  className={styles.ft}
  value={value ?? ''}
  onChange={(e) => onChange(e.target.value)}
  placeholder={field.placeholder || ''}
  readOnly={!!field.readonly}
  style={field.readonly ? { background: 'var(--card)', color: 'var(--mu)' } : undefined}
/>
        </div>
      );

    case 'select':
      // A field marked `source: "clinics"` lists the real clinics from the DB
      // (not a hardcoded list), storing the clinic id as its value.
      if (field.source === 'clinics') {
        return <ClinicSelectField field={field} value={value} onChange={onChange} id={id} labelCls={labelCls} flagDot={flagDot} />;
      }
      return (
        <div className={styles.fg}>
          <label className={labelCls} htmlFor={id}>{field.label}{flagDot}</label>
          {field.hint && <div className={styles.fieldHint}>{field.hint}</div>}
          <select
            id={id}
            className={styles.fs}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Select…</option>
            {fieldOptions(field).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );

    case 'combo': {
      // Free-text with a suggestion list (datalist): the user can pick a listed
      // value or type their own. Used for City — stops misspelling of common
      // cities without trapping a place that isn't on the list.
      const listId = `${id}-list`;
      const opts = fieldOptions(field);
      return (
        <div className={styles.fg}>
          <label className={labelCls} htmlFor={id}>{field.label}{flagDot}</label>
          {field.hint && <div className={styles.fieldHint}>{field.hint}</div>}
          <input
            id={id}
            className={`${styles.fi} ${error ? styles.fiError : ''}`}
            list={listId}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || 'Type or pick…'}
            autoComplete="off"
            aria-invalid={error ? 'true' : undefined}
          />
          <datalist id={listId}>
            {opts.map((opt) => (<option key={opt} value={opt} />))}
          </datalist>
          {error && <div className={styles.errText}>{error}</div>}
        </div>
      );
    }

    case 'radio-pills': {
      const arr = field.options || [];
      return (
        <div className={styles.fg}>
          <label className={labelCls}>{field.label}{flagDot}</label>
          {field.hint && <div className={styles.fieldHint}>{field.hint}</div>}
          <div className={styles.pillRow}>
            {arr.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`${styles.pill} ${value === opt ? styles.pillActive : ''}`}
                onClick={() => onChange(opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      );
    }

    case 'pills': {
      if (field.source === 'clinics') {
        return <ClinicPillsField field={field} value={value} onChange={onChange} labelCls={labelCls} flagDot={flagDot} />;
      }
      const arr = Array.isArray(value) ? value : [];
      const toggle = (opt) =>
        onChange(arr.includes(opt) ? arr.filter((v) => v !== opt) : [...arr, opt]);
      return (
        <div className={styles.fg}>
          <label className={labelCls}>{field.label}{flagDot}</label>
          {field.hint && <div className={styles.fieldHint}>{field.hint}</div>}
          <div className={styles.pillRow}>
            {(field.options || []).map((opt) => (
              <button
                key={opt}
                type="button"
                className={`${styles.pill} ${arr.includes(opt) ? styles.pillActive : ''}`}
                onClick={() => toggle(opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      );
    }

    case 'image':
      return (
        <div className={styles.fg}>
          <label className={labelCls}>{field.label}{flagDot}</label>
          <LogoUpload field={field} value={value} onChange={onChange} uploadCtx={uploadCtx} />
          {field.hint && <div className={styles.imgHint}>{field.hint}</div>}
        </div>
      );

    case 'checkbox':
      return (
        <div className={styles.fg}>
          <label className={styles.declarationCheck}>
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
            />
            <span>{field.label}</span>
          </label>
        </div>
      );

    case 'time':
      return (
        <div className={styles.fg}>
          <label className={labelCls}>{field.label}{flagDot}</label>
          <input
            className={styles.fi}
            type="time"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
          {field.hint && <div className={styles.infoText}>{field.hint}</div>}
        </div>
      );

    default:
      return (
        <div className={styles.fg}>
          <label className={labelCls}>{field.label}{flagDot}</label>
          <div className={styles.infoText}>Unsupported field type: {field.type}</div>
        </div>
      );
  }
}
/* ── Standard option lists — a field with `source: "states"` (a select) or
   `source: "cities"` (a combo) draws from these instead of carrying its own
   options, so every address across clinic/doctor/staff/patient forms uses one
   spelling. States is the full set of Indian states + UTs; Cities is a
   suggestion list only (combo stays typeable, so any place still works). ── */
const INDIA_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry',
];

const INDIA_CITIES = [
  'Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Thane', 'Navi Mumbai',
  'Chhatrapati Sambhajinagar', 'Solapur', 'Kolhapur', 'Amravati', 'Sangli',
  'Delhi', 'New Delhi', 'Bengaluru', 'Mysuru', 'Mangaluru', 'Hubballi',
  'Hyderabad', 'Chennai', 'Coimbatore', 'Madurai', 'Kolkata', 'Ahmedabad',
  'Surat', 'Vadodara', 'Rajkot', 'Jaipur', 'Jodhpur', 'Udaipur', 'Lucknow',
  'Kanpur', 'Varanasi', 'Agra', 'Prayagraj', 'Noida', 'Ghaziabad', 'Meerut',
  'Indore', 'Bhopal', 'Gwalior', 'Jabalpur', 'Patna', 'Ranchi', 'Jamshedpur',
  'Bhubaneswar', 'Cuttack', 'Guwahati', 'Chandigarh', 'Ludhiana', 'Amritsar',
  'Jalandhar', 'Gurugram', 'Faridabad', 'Kochi', 'Thiruvananthapuram',
  'Kozhikode', 'Thrissur', 'Visakhapatnam', 'Vijayawada', 'Panaji',
  'Raipur', 'Dehradun', 'Shimla', 'Srinagar', 'Jammu',
];

/* Resolve a field's option list — from a named `source` or its own `options`. */
function fieldOptions(field) {
  if (field.source === 'states') return INDIA_STATES;
  if (field.source === 'cities') return INDIA_CITIES;
  return field.options || [];
}

/* ── Live clinic fields — list the real clinics from the DB instead of a
   hardcoded set. Value stored is the clinic id (e.g. "AYUR-3400"), label shows
   "Name · id" so same-named clinics stay distinct. Used by the doctor form's
   Primary / Secondary clinic pickers. ── */
function useClinicOptions() {
  const { data, isLoading, isError } = useClinicsList({});
  const clinics = data ? data.pages.flatMap((p) => p.clinics || []) : [];
  return { clinics, isLoading, isError };
}

function ClinicSelectField({ field, value, onChange, id, labelCls, flagDot }) {
  const { clinics, isLoading } = useClinicOptions();
  return (
    <div className={styles.fg}>
      <label className={labelCls} htmlFor={id}>{field.label}{flagDot}</label>
      <select id={id} className={styles.fs} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">{isLoading ? 'Loading clinics…' : 'Select a clinic…'}</option>
        {clinics.map((c) => (
          <option key={c.id} value={c.id}>{c.name} · {c.id}</option>
        ))}
      </select>
    </div>
  );
}

function ClinicPillsField({ field, value, onChange, labelCls, flagDot }) {
  const { clinics, isLoading } = useClinicOptions();
  const arr = Array.isArray(value) ? value : [];
  const toggle = (cid) => onChange(arr.includes(cid) ? arr.filter((v) => v !== cid) : [...arr, cid]);
  return (
    <div className={styles.fg}>
      <label className={labelCls}>{field.label}{flagDot}</label>
      {isLoading ? (
        <div className={styles.infoText}>Loading clinics…</div>
      ) : (
        <div className={styles.pillRow}>
          {clinics.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`${styles.pill} ${arr.includes(c.id) ? styles.pillActive : ''}`}
              onClick={() => toggle(c.id)}
              title={c.id}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
      {!isLoading && clinics.length === 0 && <div className={styles.infoText}>No clinics available yet.</div>}
      {field.hint && <div className={styles.infoText}>{field.hint}</div>}
    </div>
  );
}

/* Logo / image field (B144) — preview + upload to the clinic logo endpoint.
   Stores the returned URL as the field value; the section save syncs it to the
   clinic's logo column. */
function LogoUpload({ field, value, onChange, uploadCtx }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const accept = field.accept || 'image/png,image/jpeg,image/webp,image/svg+xml';

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { setErr('Please choose an image file.'); return; }
    if (file.size > 3 * 1024 * 1024) { setErr('Image is over 3 MB — please pick a smaller one.'); return; }
    setBusy(true); setErr('');
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result); r.onerror = () => rej(new Error('Could not read the file.'));
        r.readAsDataURL(file);
      });
      const out = await uploadClinicLogo({ data: dataUrl, filename: file.name, clinicId: uploadCtx?.clinicId || undefined });
      onChange(out.url);
    } catch (e2) {
      setErr(e2?.message || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.logoField}>
      <div className={styles.logoPreview}>
        {value ? <img src={value} alt="Clinic logo" /> : <span className={styles.logoEmpty}>No logo</span>}
      </div>
      <div className={styles.logoActions}>
        <button type="button" className={styles.logoBtn} onClick={() => { setErr(''); fileRef.current?.click(); }} disabled={busy}>
          {busy ? 'Uploading…' : value ? 'Replace logo' : 'Upload logo'}
        </button>
        {value && !busy && (
          <button type="button" className={styles.logoRemove} onClick={() => onChange('')}>Remove</button>
        )}
        <input ref={fileRef} type="file" accept={accept} hidden onChange={onFile} />
        {err && <div className={styles.imgErr}>{err}</div>}
      </div>
    </div>
  );
}
